import { useRef, useEffect } from 'react';

import { Banner } from '@atlaskit/navigation-system/layout/banner';
import { ConsentBanner } from '@atlassian/browser-storage-controls';

import { useConsentBanner } from 'src/app/navigation-provider';
import SiteBanner from 'src/components/site-banner';

import {
  useHasConsentBanner,
  useSiteBannerMessage,
  useElementHeight,
} from './hooks';
import { MarketingBanner, useMarketingBanner } from './marketing-banner';

const SITE_BANNER_HEIGHT = 48;

/**
 * Navigation System Banner component for nav-v4
 *
 * Combines consent banner and site banner with dynamic height calculation.
 * Uses ResizeObserver to track consent banner height and adds it to site banner height
 * for the navigation-system Banner component.
 */
export const NavSystemBanner = (): JSX.Element => {
  // consent banner data
  const consentBannerRef = useRef<HTMLDivElement>(null);
  const hasConsentBanner = useHasConsentBanner();
  const consentBannerHeight = useElementHeight(
    consentBannerRef,
    hasConsentBanner
  );
  // useConsentBanner hook is used in a bunch of places across the app
  // but this could be refactored and removed after rollout. Keeping in
  // for now.
  const [_, consentBannerActions] = useConsentBanner();
  useEffect(() => {
    consentBannerActions.setConsentBannerHeight(consentBannerHeight);
  }, [consentBannerHeight, consentBannerActions]);

  // site banner data
  const siteMessage = useSiteBannerMessage();
  const siteBannerHeight = siteMessage ? SITE_BANNER_HEIGHT : 0;

  const { shouldDisplayMarketingBanner, dismissMarketingBanner } =
    useMarketingBanner();
  const marketingBannerHeight = shouldDisplayMarketingBanner
    ? SITE_BANNER_HEIGHT
    : 0;

  // calculate combined height to pass to nav system Banner
  const combinedHeight =
    consentBannerHeight + siteBannerHeight + marketingBannerHeight;

  return (
    <Banner height={combinedHeight}>
      {hasConsentBanner && (
        <div ref={consentBannerRef}>
          <ConsentBanner product="Bitbucket" />
        </div>
      )}
      {siteMessage && <SiteBanner siteMessage={siteMessage} />}
      {shouldDisplayMarketingBanner && (
        <MarketingBanner onDismiss={dismissMarketingBanner} />
      )}
    </Banner>
  );
};
